import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import JsBarcode from 'jsbarcode';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPrint } from '@fortawesome/free-solid-svg-icons';
import logoImage from '../assets/images/logo.jpg';
import signatureImage from '../assets/images/dclogo.png';
import { debounce } from 'lodash'; // Import debounce

const SDSRList = () => {
    const navigate = useNavigate();
    const [sdsrEntries, setSdsrEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);  // Add pagination state
    const [totalRecords, setTotalRecords] = useState(0);
    useEffect(() => {
        const fetchEntries = async () => {
            try {
                const response = await fetch('https://chaman.gob.pk/eac_backend/s-dsr.php', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        action: 'get_records',
                        page: page,   // Fetch based on page number
                        limit: 1000,   // Fetch 100 records at a time
                    }),
                });

                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }

                const data = await response.json();
                if (Array.isArray(data)) {
                    setSdsrEntries(data);
                } else {
                    setSdsrEntries([]);
                    console.error('Expected array but got:', data);
                }
            } catch (err) {
                setError('Failed to fetch SDSR entries');
            } finally {
                setLoading(false);
            }
        };
        fetchEntries();
    }, []);

    const handleDelete = async (token_id) => {
        if (window.confirm(`Are you sure you want to delete the entry with Token ID: ${token_id}?`)) {
            try {
                const response = await axios.post('https://chaman.gob.pk/eac_backend/s-dsr.php', {
                    action: 'delete',
                    token_id,
                });
                if (response.data.status === 'success') {
                    setSdsrEntries((prevEntries) => prevEntries.filter((entry) => entry.token_id !== token_id));
                    alert('Entry deleted successfully.');
                } else {
                    alert('Error deleting entry: ' + response.data.message);
                }
            } catch (error) {
                alert('Error deleting entry');
            }
        }
    };

    const handleEdit = (entry) => {
        navigate('/dashboard/edit-special-dsr', { state: { entry } });
    };

    const handlePrint = async (entry) => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        // Add logo and header
        const imageWidth = 0.8 * 20; // 0.8 inches
        const imageHeight = 0.7 * 20; // 0.6 inches
        const xCenter = (pageWidth - imageWidth) / 2;
        doc.addImage(logoImage, 'PNG', xCenter, 10, imageWidth, imageHeight);
        doc.setFontSize(21);
        doc.text('OFFICE OF THE DEPUTY COMMISSIONER CHAMAN', 14, 37);

        // Add DSR No. and Date
        doc.setFontSize(10);
        doc.text(`DSR No.: ${entry.dsr_no}`, 14, 42);
        doc.text(`Date: ${entry.dated}`, pageWidth - 40, 42);

        // Add "To" section
        doc.setFontSize(10); // Matching the font size for "To" and statement section
        doc.text('To:', 14, 50);
        doc.text('The Director General Balochistan Levies Force', 50, 55);
        doc.text('Quetta', 50, 60);

        // Add Subject Label
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');  // Use normal font for the label "SUBJECT:"
        doc.text('SUBJECT:', 14, 70);

        const subjectX = 50;
        const maxSubjectWidth = pageWidth - 70;
        const subjectText = (entry.subject || '').toUpperCase();  // Convert the subject to uppercase

        // Split the subject text into lines to fit within the max width
        const subjectLines = doc.splitTextToSize(subjectText, maxSubjectWidth);

        // Apply bold style to the subject text only
        doc.setFont('helvetica', 'bold').setFontSize(11);
        doc.text(subjectLines, subjectX, 70);

        // Add underline for the subject
        subjectLines.forEach((line, index) => {
            const lineWidth = doc.getTextWidth(line);
            const lineY = 70 + (index * 7);  // Adjust vertical spacing if needed
            doc.setLineWidth(0.5); // Set line width for the underline
            doc.line(subjectX, lineY + 1, subjectX + lineWidth, lineY + 1);  // Underline the text
        });

        // Reset font to normal for the statement
        doc.setFont('helvetica', 'normal').setFontSize(10);

        // Add the statement text
        const statementY = 70 + subjectLines.length * 7;
        const statementLines = doc.splitTextToSize(entry.statement || '', maxSubjectWidth);
        doc.text(statementLines, 50, statementY);



        // Calculate Y position for the statement
        // const statementY = 70 + subjectLines.length * 5;
        // const statementLines = doc.splitTextToSize(entry.statement || '', maxSubjectWidth);
        // doc.text(statementLines, 50, statementY);
        // Table Section
        const tableY = statementY + statementLines.length * 4.5 + 0.5;
        doc.autoTable({
            head: [['S.No.', 'Event', 'Description']],
            body: [
                ['1', 'Location', entry.location],
                ['2', 'Timings', entry.timings],
                ['3', 'Approximate Strength', entry.approximate_strength],
                ['4', 'Prominent Speakers', entry.prominent_speakers],
                ['5', 'Hate Speech / Anti-State Sentiments by Speakers', entry.hate_speech],
                ['6', 'Security Arrangements', entry.security_arrangements],
                ['7', 'Any Special Activity', entry.special_activity],
            ],
            startY: tableY,
            theme: 'grid',
            headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] },
            styles: { fontSize: 9, textColor: [0, 0, 0] },
            columnStyles: { 0: { halign: 'center' } },
        });

        // Footer Section (Adjust dynamically based on the table)
        const footerY = Math.max(doc.autoTable.previous.finalY + 5, pageHeight - 60);

        // Text position for "DEPUTY COMMISSIONER"
        const deputyTextX = 148;  // X position for "DEPUTY COMMISSIONER"
        const deputyTextY = footerY; // Y position for "DEPUTY COMMISSIONER"

        // Signature Image Position
        const signatureWidth = 0.8 * 25;  // Width of the signature (in cm)
        const signatureHeight = 0.6 * 25;  // Height of the signature (in cm)

        // X and Y position for the signature image
        const signatureX = 163;  // X position for the signature (align with "DEPUTY COMMISSIONER")
        const signatureY = deputyTextY - signatureHeight - 1;  // Y position for the signature (above the "DEPUTY COMMISSIONER" text)

        // Add the signature image
        doc.addImage(signatureImage, 'PNG', signatureX, signatureY, signatureWidth, signatureHeight);

        // Add "DEPUTY COMMISSIONER" and "CHAMAN" text
        doc.setFontSize(10).setFont('helvetica', 'normal');  // Same font as the statement
        doc.text('DEPUTY COMMISSIONER', deputyTextX, deputyTextY);
        doc.text('CHAMAN', 163, footerY + 5);

        // "Copy To" Section
        const copyToY = footerY + 10;  // Position the "Copy To" section just below the "DEPUTY COMMISSIONER" text
        doc.text('Copy To:', 14, copyToY);

        // Define the static names for the "Copy To" section
        const copyToNames = [
            '1. The Additional Chief Secretary, Home & T.A Dept; Balochistan',
            '2. The Commissioner Quetta Division, Quetta',
            '3. The Additional Secretary (Staff) to the Chief Secretary, Balochistan',
        ];

        // Split each line to fit within the page width and position them
        const copyToLines = doc.splitTextToSize(copyToNames.join('\n'), pageWidth - 25);

        // Add the lines to the document, starting from the "Copy To" section
        doc.text(copyToLines, 25, copyToY + 5);


        // Barcode
        const barcodeCanvas = document.createElement('canvas');
        JsBarcode(barcodeCanvas, entry.token_id, { width: 1, height: 15, displayValue: false });
        const barcodeDataURL = barcodeCanvas.toDataURL('image/png');
        doc.addImage(barcodeDataURL, 'PNG', 82, pageHeight - 30, 50, 20);

        // Open the generated PDF
        window.open(doc.output('bloburl'));
    };

    const exportToExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(sdsrEntries);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'SDSR Entries');
        XLSX.writeFile(workbook, 'SDSRList.xlsx');
    };

    const loadMoreEntries = () => {
        if (sdsrEntries.length < totalRecords) {
            setPage(page + 1);
        }
    };


    if (loading) return <div>Loading entries...</div>;
    if (error) return <div>Error: {error}</div>;

    return (
        <div className="sdsr-list">
            <div className="listsearch">
                <input
                    id="lstsea"
                    type="text"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)} // Use direct search term update
                />
            </div>
            <table>
                <thead>
                    <tr>
                        <th>Token ID</th>
                        <th>DSR No.</th>
                        <th>Dated</th>
                        <th>Subject</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {sdsrEntries
                        .filter(entry =>
                            Object.values(entry).some(value =>
                                String(value).toLowerCase().includes(searchTerm.toLowerCase())
                            )
                        )
                        .map(entry => (
                            <tr key={entry.token_id}>
                                <td>{entry.token_id}</td>
                                <td>{entry.dsr_no}</td>
                                <td>{entry.dated}</td>
                                <td>{entry.subject}</td>
                                <td>
                                    <div className="listbuttons">
                                        <button id="lstbtn" onClick={() => handlePrint(entry)} title="Print">
                                            <FontAwesomeIcon icon={faPrint} />
                                        </button>
                                        {/* <button id="lstbtn" onClick={() => handleEdit(entry)} title="Edit">
                                            <FontAwesomeIcon icon={faEdit} />
                                        </button>
                                        <button id="lstbtn" onClick={() => handleDelete(entry.token_id)} title="Delete">
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button> */}
                                    </div>
                                </td>
                            </tr>
                        ))}
                </tbody>
            </table>

            {/* Load more button */}
            {/* If you need a Load More feature, you can add it here */}
        </div>
    );
};

export default SDSRList;
